/**
 * The price book's WRITE calls (T651 M5) — agree a price, change a price.
 *
 * ⛔ WHY THIS IS ITS OWN MODULE AND NOT AN APPEND TO lib/actions.ts. That file
 * froze at T651 M2 so that three milestones filling three sibling screens could
 * run without editing the same declarations; the stated hazard is "two adjacent
 * appends to a shared client file". A per-area module produces ZERO appends to
 * any shared client file. This is the same ruling lib/account-actions.ts and
 * lib/destination-actions.ts record, applied identically and for the third time.
 *
 * ⛔ THE COPY COMES FROM THE STATUS, NEVER THE SERVER BODY (SEC-5), matching
 * both sibling modules. The route sentences are terse and developer-shaped;
 * these are the ones an operator reads.
 *
 * ⛔ AND THIS FILE NEVER DECIDES A PRICE. It posts one the operator typed and
 * reports what the server said. Reading a figure OUT of the book to answer "what
 * does this account pay to go there?" is D-RESOLVE's job and belongs to the
 * single-pair arm of `GET /api/account-prices` — see
 * core/data/account-prices-repo.ts, and the client-side pin in
 * test/core/account-prices-resolve.spec.ts that stops a screen doing it here.
 */
import { ApiError, postJson } from './api.js';
import { isRegistryErrorCode } from '../../shared/registry-error-schema.js';

/**
 * What a price write answers, beyond success.
 *
 * ⭐ `offerable` IS A REPORTED FACT, NOT A FAILURE. The write succeeded and the
 * price is stored and resolvable; `false` means the account or the destination
 * is currently out of use, so no picker will offer this pair for NEW work. The
 * server measures it (it holds both rows); the copy for it lives here with the
 * rest of the operator wording, which is the split handlePostRateEntry's
 * `searchable` fact already uses.
 */
export type PriceResult =
  | { ok: true; offerable: boolean; changed?: boolean }
  | { ok: false; message: string };

function priceFailure(e: unknown, verb: string, kind: 'add' | 'save'): string {
  const status = e instanceof ApiError ? e.status : 0;
  if (status === 409) {
    // ⚠️ ONE STATUS, TWO CAUSES, TOLD APART BY AN EXPLICIT `kind` RATHER THAN BY
    // COMPARING THE VERB PROSE. The prose is operator copy and will be edited;
    // keying behaviour on it means a wording change silently reverts the race
    // message to the duplicate-pair one. On an
    // ADD, a 409 means the pair already has a price. On a SAVE it means somebody
    // else changed this price while she was typing (board 710) - the write was
    // refused rather than overwriting their decision, so the copy has to send
    // her back to look rather than just tell her to retry.
    return kind === 'save'
      ? 'Somebody else changed that price while you were editing it. Reload to see the price now, then change it again if you still want to.'
      : 'There is already a price for that home and that destination. Change the one on the list instead of adding a second.';
  }
  if (status === 404) {
    return 'That home or that destination is no longer on the list. Reload and try again.';
  }
  if (status === 400) {
    return 'Check the details. A price needs a home, a destination, an amount above zero and a short reason.';
  }
  return `Could not ${verb}. Try again in a moment.`;
}

/**
 * Agree a price for a pair that does not have one.
 *
 * ⛔ BOTH KEYS ARE IDS, NEVER NAMES. The form picks an account and a destination
 * from the two catalogues the screen was handed; nothing here types a name and
 * hopes the server finds it. That is the whole reason PriceList.tsx's prop
 * contract carries `accounts` and `destinations` as well as `prices`.
 */
export async function createAccountPrice(
  accountId: number,
  destinationId: number,
  priceCents: number,
  note: string,
): Promise<PriceResult> {
  try {
    const res = await postJson<{ offerable: boolean }>(
      '/api/account-prices', { accountId, destinationId, priceCents, note },
    );
    return { ok: true, offerable: res.offerable };
  } catch (e) {
    return { ok: false, message: priceFailure(e, 'add that price', 'add') };
  }
}

/**
 * Change an agreed price.
 *
 * ⚠️ `changed: false` IS A SUCCESS, and it means the figure was already that.
 * The server refuses to file an "old 80.00 → new 80.00" row into a journal with
 * no erasure path, so saving an untouched price writes nothing at all — and the
 * screen says so rather than claiming a change that did not happen.
 */
export async function setAccountPrice(
  accountId: number,
  destinationId: number,
  priceCents: number,
  note: string,
): Promise<PriceResult> {
  try {
    const res = await postJson<{ offerable: boolean; changed: boolean }>(
      '/api/account-prices/update', { accountId, destinationId, priceCents, note },
    );
    return { ok: true, offerable: res.offerable, changed: res.changed };
  } catch (e) {
    return { ok: false, message: priceFailure(e, 'save that price', 'save') };
  }
}

/**
 * ⛔ A HAND-WRITTEN MIRROR of `PriceRemoveRefusalCode` in `server/app.ts`
 * (board 2705), for the reason `QuotePriceRefusalCode` in actions.ts is one: that
 * module pulls in `pg`, which would drag a node-only dependency into the browser
 * bundle. Not free to rot: `test/server/account-price-write-routes.spec.ts`
 * asserts the two unions are the same type, so a member gained, lost or
 * misspelt on either side fails the typecheck.
 */
export type PriceRemoveRefusalCode = 'price_last_on_list' | 'price_changed_meanwhile';

/**
 * The sentence per removal refusal, chosen HERE from the wire code (SEC-5): the
 * server's sentence never reaches the screen. `Record` over the mirror, so `tsc`
 * refuses a code with no sentence.
 * ⛔ NO EM DASH AND NO CURRENCY SYMBOL (`check:copy`), and no home or place is
 * named: the sentence is shown beside a block that already says which.
 */
const REMOVE_REFUSAL_COPY: Record<PriceRemoveRefusalCode, string> = {
  price_last_on_list:
    'That is the last price on this home\'s list, so it was not removed. Add the destination that '
    + 'replaces it first, then remove this one.',
  price_changed_meanwhile:
    'Somebody else changed that price while you were removing it, so nothing was removed. Reload '
    + 'to see the list now, then remove it again if you still want to.',
};

function removeRefusalCodeOf(body: unknown): PriceRemoveRefusalCode | null {
  if (typeof body !== 'object' || body === null || !('code' in body)) return null;
  const code = (body as { code: unknown }).code;
  return typeof code === 'string' && Object.hasOwn(REMOVE_REFUSAL_COPY, code)
    ? code as PriceRemoveRefusalCode
    : null;
}

/** What a removal answers. Success carries nothing more: the screen reloads. */
export type RemoveResult = { ok: true } | { ok: false; message: string };

/**
 * Take a destination off ONE home's agreed prices (board 2705).
 *
 * ⛔ IT REACHES THE PORTAL ONLY WHEN SHE SENDS: the server retires the pair in
 * the kiosk's book, and the home keeps offering it until "Send prices to the
 * portal" is pressed for that home. The screen says so; this function only
 * reports what the server said.
 * ⛔ THE COPY IS THIS FILE'S, keyed off the CODE, then `account_busy` through
 * the shared registry guard, then the status, then a generic sentence.
 */
export async function removeAccountPrice(
  accountId: number,
  destinationId: number,
  note: string,
): Promise<RemoveResult> {
  try {
    await postJson<{ ok: true }>('/api/account-prices/remove', { accountId, destinationId, note });
    return { ok: true };
  } catch (e) {
    const status = e instanceof ApiError ? e.status : 0;
    const body = e instanceof ApiError ? e.body : null;
    const code = removeRefusalCodeOf(body);
    if (code !== null) return { ok: false, message: REMOVE_REFUSAL_COPY[code] };
    const shared = typeof body === 'object' && body !== null && 'code' in body
      ? (body as { code: unknown }).code
      : null;
    if (isRegistryErrorCode(shared) && shared === 'account_busy') {
      return {
        ok: false,
        message: 'Another change is being saved right now. Nothing was removed. Wait a moment and press remove again.',
      };
    }
    if (status === 404) {
      return { ok: false, message: 'That price is no longer on the list. Reload to see the list now.' };
    }
    if (status === 400) {
      return { ok: false, message: 'Say why, in a few words. It is kept with your name against this change.' };
    }
    return { ok: false, message: 'Could not remove that price. Try again in a moment.' };
  }
}

/**
 * Euro typed by an operator, to integer cents — or null if it is not a price.
 *
 * ⛔ IT REFUSES RATHER THAN ROUNDS, and that is the same posture the server's
 * `parseMoneyCents` takes for the same reason: a figure that is not a price must
 * not be turned into a plausible one. "80", "80.5" and "80.50" are prices;
 * "eighty", "-80", "0" and "80.505" are not.
 *
 * ⚠️ DECLARED HERE RATHER THAN IMPORTED FROM THE SERVER, exactly as the three
 * admin screens re-declare their name fold and for the same reason: the server
 * module pulls in `pg` and would drag a node-only dependency into the browser
 * bundle. Drift can only ever cost the operator a round trip — the server's 400
 * stays authoritative, so a parse that disagreed could never turn a refusal into
 * an accept.
 */
export function parseEuroToCents(raw: string): number | null {
  const text = raw.trim().replace(/^\+/, '');
  if (!/^\d+(\.\d{1,2})?$/.test(text)) return null;
  // ⛔ Math.round on the SCALED value, not on a float multiplication left bare:
  // 80.29 * 100 is 8028.999999999999 in IEEE 754, and a bare truncation would
  // store 80.28. The repo has a whole file of notes about this class of bug.
  const cents = Math.round(Number(text) * 100);
  return Number.isSafeInteger(cents) && cents > 0 ? cents : null;
}
